import { useSuspenseQuery } from "@tanstack/react-query";
import sortBy from "lodash/sortBy";
import { useOnPropagate } from "@/frontend/@api/PropagateProvider";
import { client } from "@/frontend/@api/trpcClient";
import { usePrimeFromCollection } from "@/frontend/@api/usePrimeFromCollection";

const GET_ASSESSMENT_KEY = "getAssessment"; // used by multiple hooks

export const useAssessment = (firmId: string, assessmentId: string) => {
    const key = GET_ASSESSMENT_KEY;

    const { data: assessment } = useSuspenseQuery({
        queryKey: [key, firmId, assessmentId],
        queryFn: () =>
            client.assessment.getAssessment.query({
                firmId,
                assessmentId
            })
    });

    useOnPropagate(key, p => {
        p.onSingleDefaults("assessment", x => [key, x.firmId, x.id]);
        p.onDeleteInvalidate("firm", x => [key, x.id]);
    });

    return assessment;
};

export const useFirmAssessments = (firmId: string) => {
    const key = "getFirmAssessments";

    const { data: rawAssessments } = useSuspenseQuery({
        queryKey: [key, firmId],
        queryFn: () =>
            client.assessment.getAssessmentsForFirm.query({
                firmId
            })
    });

    const assessments = sortBy(rawAssessments, x => x.createdAt).reverse();

    usePrimeFromCollection(assessments, x => [
        GET_ASSESSMENT_KEY,
        x.firmId,
        x.id
    ]);

    useOnPropagate(key, p => {
        p.onCollectionDefaults("assessment", x => [key, x.firmId]);
        p.onDeleteInvalidate("firm", x => [key, x.id]);
    });

    return assessments;
};

export const useAssessmentTypeAssessments = (assessmentTypeId: string) => {
    const key = "getAssessmentTypeAssessments";

    const { data: rawAssessments } = useSuspenseQuery({
        queryKey: [key, assessmentTypeId],
        queryFn: () =>
            client.assessment.getAssessmentsByType.query({
                assessmentTypeId
            })
    });

    const assessments = sortBy(rawAssessments, x => x.createdAt).reverse();

    useOnPropagate(key, p => {
        p.onCollectionDefaults("assessment", x => [key, x.assessmentTypeId]);
        p.onDeleteInvalidate("assessmentType", x => [key, x.id]);
    });

    return assessments;
};

// re-export for prefetching (react-compiler eslint doesn't like passing hooks as reference)
export const reexported__useAssessment = useAssessment;
export const reexported__useFirmAssessments = useFirmAssessments;
export const reexported__useAssessmentTypeAssessments =
    useAssessmentTypeAssessments;
